import apiFetch from '@scale/frontend-lib/api-fetch';
import _ from 'lodash';
import FormData from 'form-data';
import { DocumentTypeTaxonomy } from '@scale/shared/types/documentAIGo/documentTypes';
import { Project } from 'dashboard/types';
import { TeamRole } from '@scale/shared/defaults';
import { TeamListJSON } from '@scale/shared/types/api/internal/document-ai-go';
import { DocAIGoTrackEventParams } from '@scale/shared/types/documentAIGo/analyticsTypes';
import { DocumentAIGoMonthlyBillingInfo } from '@scale/shared/types/documentAIGo/billing';
import { BillingParams } from '@scale/shared/types/endpoints/customer/user/billing';
import type { Types } from 'mongoose';

export const fetchProjects = async (): Promise<Project[]> => {
  const fetchProjectsResult = await apiFetch(null, 'internal/document-ai-go/projects', {
    method: 'GET',
  });

  return fetchProjectsResult.docs;
};

export const createProject = async (
  project: Project,
  visibleFields: string[],
): Promise<Project> => {
  const createProjectResult = await apiFetch(null, 'internal/document-ai-go/create_project', {
    method: 'POST',
    body: {
      name: project.name,
      taskType: project.type,
      projectType: project.projectType,
      documentType: project.documentType,
      visibleFields,
    },
  });

  return createProjectResult;
};

export const createSampleProject = async (): Promise<Project> => {
  const sampleProjectResult = await apiFetch(
    null,
    'internal/document-ai-go/create_sample_project',
    {
      method: 'POST',
    },
  );

  return sampleProjectResult;
};

export const updateProjectSettingsAndParams = async (
  projectId: string,
  projectParams?: DocumentTypeTaxonomy,
  taskId?: string,
  name?: string,
): Promise<DocumentTypeTaxonomy> => {
  const updateProjectParamsResult = await apiFetch(
    null,
    'internal/document-ai-go/update_project_settings_and_params',
    {
      method: 'POST',
      body: {
        projectId,
        projectParams,
        taskId,
        name,
      },
    },
  );

  return updateProjectParamsResult;
};

export const getProjectParams = async (projectId: string): Promise<DocumentTypeTaxonomy> => {
  const getProjectParamsResult = await apiFetch(
    null,
    `internal/document-ai-go/project_params/${projectId}`,
    {
      method: 'GET',
    },
  );

  return getProjectParamsResult;
};

export const uploadFile = async (
  projectId: string,
  file: File,
): Promise<{ attachmentDataUrl: string; filename: string; taskId: Types.ObjectId }> => {
  const formData = new FormData();
  formData.append('asset', file);
  formData.append('projectId', projectId);
  const uploadResult = await apiFetch(null, 'internal/document-ai-go/upload_file', {
    method: 'POST',
    body: formData,
  });

  return uploadResult;
};

export const createDocumentTask = async (
  projectId: string,
  attachmentDataUrl: string,
  filename: string,
  taskId: Types.ObjectId,
): Promise<{ taskId: string }> => {
  const createResult = await apiFetch(null, 'internal/document-ai-go/create_document_task', {
    method: 'POST',
    body: {
      projectId,
      attachmentDataUrl,
      filename,
      taskId,
    },
  });

  return createResult;
};

export const getTeamMembers = async (): Promise<TeamListJSON[]> => {
  const getTeamMembersResult = await apiFetch(null, 'internal/document-ai-go/team', {
    method: 'GET',
  });

  return getTeamMembersResult;
};

export const inviteMember = async (email: string, teamRole: TeamRole): Promise<{ url: string }> => {
  const inviteMemberResult = await apiFetch(null, 'internal/document-ai-go/inviteMember', {
    method: 'POST',
    body: {
      email,
      teamRole,
    },
  });

  return inviteMemberResult;
};

export const acceptInvite = async (teamToken: string): Promise<void> => {
  await apiFetch(null, 'internal/document-ai-go/acceptInvite', {
    method: 'POST',
    body: {
      teamToken,
    },
  });
};

export const createAuditBatch = async (projectId: string, taskIds: string[]): Promise<string> => {
  const auditBatchId = await apiFetch(null, 'internal/document-ai-go/auditBatch', {
    method: 'POST',
    body: {
      projectId,
      taskIds,
    },
  });

  return auditBatchId;
};

export const getAuditBatchTaskIds = async (auditBatchId: string): Promise<string[]> => {
  const taskIds = await apiFetch(null, `internal/document-ai-go/auditBatch/${auditBatchId}`, {
    method: 'GET',
  });

  return taskIds;
};

export async function trackEvent(params: DocAIGoTrackEventParams) {
  return await apiFetch(null, `internal/document-ai-go/analytics/track_event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
}

export const getMonthlyBillingInfo = async ({
  startDate,
  endDate,
}: BillingParams): Promise<DocumentAIGoMonthlyBillingInfo> => {
  const params = {
    startDate,
    endDate,
  };
  const query = _.omitBy(params, _.isNil);
  const monthlyBillingInfo = await apiFetch(
    null,
    `internal/document-ai-go/billing/monthlyBalanceInfo`,
    {
      query,
    },
  );

  return monthlyBillingInfo;
};
